import Component from '@glimmer/component';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import {
  scanStatusTone,
  type ScanStatusTone,
} from 'scaleflow/utils/scan-status';

/** `lg` is used by the leading status column, which reads as the row's headline. */
export type ScanStatusDotSize = 'default' | 'lg';

type Args = {
  status?: string | null;
  size?: ScanStatusDotSize;
};

const SIZE_CLASS: Record<ScanStatusDotSize, string> = {
  default: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
};

/**
 * The palette separates "the pipeline is working" from "the pipeline is
 * waiting". Indigo means work under way. Amber means waiting, and the pulse
 * distinguishes the two kinds: pending is static (queued, nobody needs to do
 * anything) while in-review pulses (blocked on a human approving the product
 * config — it will sit there until someone acts). Failure states share the red
 * family — a genuine failure at full strength, a timeout in a lighter shade,
 * because a run that ran out of time is retryable in a way a failure is not.
 * Aborted is neutral gray: nothing went wrong, an operator called it off.
 */
const DOT_CLASS: Record<ScanStatusTone, string> = {
  completed: 'bg-green-500',
  'in-progress': 'bg-indigo-500 animate-pulse',
  'in-review': 'bg-amber-500 animate-pulse',
  failed: 'bg-red-500',
  'timed-out': 'bg-red-300',
  aborted: 'bg-gray-400',
  pending: 'bg-amber-500',
  unknown: '',
};

export default class ScansStatusDotComponent extends Component<Args> {
  @service declare intl: IntlService;

  get tone(): ScanStatusTone {
    return scanStatusTone(this.args.status);
  }

  get isUnknown(): boolean {
    return this.tone === 'unknown';
  }

  get dotClass(): string {
    return DOT_CLASS[this.tone];
  }

  get sizeClass(): string {
    return SIZE_CLASS[this.args.size ?? 'default'];
  }

  get label(): string {
    return this.intl.t(`scans.list.status.${this.tone}`);
  }
}
